import { useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppShell,
  Group,
  Text,
  Menu,
  Avatar,
  UnstyledButton,
  Box,
  Tabs,
  Burger,
  Drawer,
  Stack,
  NavLink as MantineNavLink,
  Image,
} from '@mantine/core';
import { useDisclosure, useElementSize } from '@mantine/hooks';
import { LogOut, User, Info } from 'react-feather';
import { useAuthStore } from '../../stores/auth';
import { usePersonaStore } from '../../stores/persona';
import { useImpersonationStore } from '../../stores/impersonation';
import { PersonaSwitcher } from '../PersonaSwitcher';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { AboutModal } from '../common/AboutModal';
import classes from './CoachLayout.module.css';
import medleyLogoWhite from '../../assets/medley-logo-white.png';
import { AppVersion } from '../common/AppVersion';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

// Navigation items by coach type
const MEDLEY_COACH_NAV_ITEMS = [
  { path: '/coach', label: 'Dashboard' },
  { path: '/coach/cohorts', label: 'My Cohorts' },
  { path: '/coach/compensation', label: 'Compensation' },
];

const PARTNER_COACH_NAV_ITEMS = [
  { path: '/coach/cohorts', label: 'My Cohorts' },
];

// Routes partner coaches are allowed to access (cohort/session/attendance only)
const PARTNER_ALLOWED_PATHS = ['/coach/cohorts'];

export function CoachLayout() {
  const { user, logout } = useAuthStore();
  const { isAdmin } = usePersonaStore();
  // MED-1383: a coach must never see "Medley Admin" in the tab.
  useDocumentTitle('Medley Coach');
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);
  const [aboutModalOpened, { open: openAboutModal, close: closeAboutModal }] = useDisclosure(false);

  const { clearImpersonation } = useImpersonationStore();

  const isPartnerCoach = user?.coach_type === 'partner';
  const navItems = isPartnerCoach ? PARTNER_COACH_NAV_ITEMS : MEDLEY_COACH_NAV_ITEMS;

  const handleLogout = async () => {
    clearImpersonation();
    await logout();
    navigate('/coach/login');
  };

  // Get user initials for avatar
  const userInitials = user?.initials || user?.first_name?.charAt(0) || 'U';
  const userName = user?.display_name || `${user?.first_name} ${user?.last_name}` || 'User';
  const userRole = isPartnerCoach ? 'Group Leader' : 'Executive Coach';

  // Redirect partner coaches away from restricted routes
  useEffect(() => {
    if (isPartnerCoach) {
      const isAllowed = PARTNER_ALLOWED_PATHS.some(
        path => location.pathname.startsWith(path),
      );
      if (!isAllowed) {
        navigate('/coach/cohorts', { replace: true });
      }
    }
  }, [isPartnerCoach, location.pathname, navigate]);

  // Handle mobile nav link click
  const handleMobileNavClick = (path: string) => {
    navigate(path);
    closeMobileNav();
  };

  // Check if path is active (exact match for dashboard, startsWith for others)
  const isPathActive = (path: string) => {
    if (path === '/coach') {
      return location.pathname === '/coach';
    }
    return location.pathname.startsWith(path);
  };

  // Measure the banner, because its height changes with its text and the
  // screen width. A fixed height let the page cover the nav (MDF-251).
  const { ref: bannerRef, height: bannerHeight } = useElementSize();
  const headerHeight = 60 + bannerHeight;

  return (
    <AppShell header={{ height: headerHeight }} padding={0}>
      {/* Header */}
      <AppShell.Header className={classes.header} style={{ height: headerHeight }}>
        <div ref={bannerRef}>
          <ImpersonationBanner />
        </div>
        <Group h={60} px="md" justify="space-between">
          {/* Left: Burger (mobile), Logo and Navigation */}
          <Group gap="md">
            {/* Mobile burger menu */}
            <Burger
              opened={mobileNavOpened}
              onClick={toggleMobileNav}
              hiddenFrom="md"
              size="sm"
              color="white"
            />

            <Group gap="xs" className={classes.logo} onClick={() => navigate('/coach')}>
              <Image src={medleyLogoWhite} alt="Medley" h={24} w="auto" fit="contain" />
            </Group>

            {/* Navigation Tabs - hidden on mobile */}
            <Tabs
              variant="pills"
              classNames={{
                root: classes.tabsRoot,
                list: classes.tabsList,
                tab: classes.tab,
              }}
              value={location.pathname}
              visibleFrom="md"
            >
              <Tabs.List>
                {navItems.map(item => (
                  <NavLink key={item.path} to={item.path} className={classes.navLink}>
                    {() => (
                      <Tabs.Tab value={item.path} data-active={isPathActive(item.path) || undefined}>
                        {item.label}
                      </Tabs.Tab>
                    )}
                  </NavLink>
                ))}
              </Tabs.List>
            </Tabs>
          </Group>

          {/* Right: Persona Switcher (for admins) and User Menu */}
          <Group gap="md">
            {/* Persona Switcher - only visible for admins viewing as coach */}
            {isAdmin && <Box visibleFrom="md"><PersonaSwitcher /></Box>}

            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <UnstyledButton className={classes.userButton}>
                  <Group gap="sm">
                    <Avatar
                      size="sm"
                      radius="xl"
                      color="coachNavy"
                      src={user?.avatar_url}
                    >
                      {userInitials}
                    </Avatar>
                    <Box visibleFrom="sm">
                      <Text size="sm" fw={500} c="white" lh={1.2}>
                        {userName}
                      </Text>
                      <Text size="xs" c="rgba(255,255,255,0.7)" lh={1.2}>
                        {userRole}
                      </Text>
                    </Box>
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>Account</Menu.Label>
                {!isPartnerCoach && (
                  <Menu.Item
                    leftSection={<User size={14} />}
                    onClick={() => navigate('/coach/profile')}
                  >
                    My Profile
                  </Menu.Item>
                )}
                <Menu.Item
                  leftSection={<Info size={14} />}
                  onClick={openAboutModal}
                >
                  About
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<LogOut size={14} />}
                  onClick={handleLogout}
                >
                  Logout
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      {/* Mobile Navigation Drawer */}
      <Drawer
        opened={mobileNavOpened}
        onClose={closeMobileNav}
        size="xs"
        padding="md"
        title={
          <Image src={medleyLogoWhite} alt="Medley" h={20} w="auto" fit="contain" style={{ filter: 'invert(1) brightness(0.3)' }} />
        }
        hiddenFrom="md"
        zIndex={1000}
      >
        <Stack gap="xs">
          {/* Persona Switcher in drawer for mobile admins */}
          {isAdmin && (
            <Box mb="md">
              <PersonaSwitcher />
            </Box>
          )}

          {navItems.map(item => (
            <MantineNavLink
              key={item.path}
              label={item.label}
              active={isPathActive(item.path)}
              onClick={() => handleMobileNavClick(item.path)}
              className={classes.mobileNavLink}
            />
          ))}
          {!isPartnerCoach && (
            <MantineNavLink
              label="My Profile"
              active={location.pathname === '/coach/profile'}
              onClick={() => handleMobileNavClick('/coach/profile')}
              className={classes.mobileNavLink}
            />
          )}
        </Stack>
      </Drawer>

      {/* Main Content */}
      <AppShell.Main className={classes.main}>
        <Outlet />
      </AppShell.Main>

      {/* MED-1360: the version line follows the page content, so it never
          covers it. AppVersion hides it where it would add a scroll. */}
      <AppVersion />

      {/* About Modal */}
      <AboutModal opened={aboutModalOpened} onClose={closeAboutModal} />
    </AppShell>
  );
}

export default CoachLayout;
